import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Crown, Clock, FileSpreadsheet, Package, Truck,
  Calendar, Image as ImageIcon, ChevronRight, Loader2, Search,
  ShieldCheck, Shield, Hammer, BarChart3, TrendingUp, Star, LineChart, Layers,
} from "lucide-react";

const TIPO_LABELS = {
  AVERIA: "Avería",
  SERVICIO_PUNTUAL: "Serv. Puntual",
  OBRA: "Obra",
  MANTENIMIENTO: "Mantenimiento",
  VARIOS: "Varios",
};

export default function SuperStats() {
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState("users");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sadmin/stats/by-user", {
        params: {
          fecha_desde: fechaDesde || undefined,
          fecha_hasta: fechaHasta || undefined,
        },
      });
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // initial

  const filtered = useMemo(() => {
    if (!data?.users) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.users;
    return data.users.filter(
      (u) => u.name.toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q),
    );
  }, [data, search]);

  const filteredOps = useMemo(() => {
    if (!data?.operarios_top) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.operarios_top;
    return data.operarios_top.filter((o) => o.nombre.toLowerCase().includes(q));
  }, [data, search]);

  return (
    <div className="space-y-6" data-testid="super-stats">
      <header>
        <div className="label-up text-[#FF4500] inline-flex items-center gap-1.5">
          <Crown size={12} /> Solo super-admin
        </div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
          Estadísticas por persona
        </h1>
        <p className="text-zinc-600 text-sm mt-1">
          Análisis detallado de cada usuario: partes, horas, materiales, obras y actividad.
        </p>
      </header>

      {/* Filters */}
      <section className="brutal-card p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label-up block mb-1.5">Desde</label>
            <input type="date" className="brutal-input w-full"
              value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
              data-testid="stats-from" />
          </div>
          <div>
            <label className="label-up block mb-1.5">Hasta</label>
            <input type="date" className="brutal-input w-full"
              value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
              data-testid="stats-to" />
          </div>
          <div className="flex items-end">
            <button onClick={load} disabled={loading}
              data-testid="stats-apply"
              className="brutal-btn-dark w-full inline-flex items-center justify-center gap-2">
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Aplicar"}
            </button>
          </div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="stats-search"
            className="brutal-input w-full pl-9"
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </section>

      {/* Comparative chart */}
      <ComparativeChart data={data?.operarios_top || []} meName={me?.name} />

      {/* Service mix per operario */}
      <ServiceMixChart data={data?.operarios_top || []} meName={me?.name} />

      {/* Monthly evolution */}
      <MonthlyEvolutionChart data={data?.operarios_top || []} meName={me?.name}
        fechaDesde={fechaDesde} fechaHasta={fechaHasta} />

      {/* Tabs */}
      <div className="flex gap-2 text-sm font-semibold">
        <button
          onClick={() => setTab("users")}
          data-testid="tab-users"
          className={`h-10 px-4 rounded-xl border ${tab === "users"
            ? "bg-[#0B2A4A] text-white border-[#0B2A4A]"
            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
          Usuarios registrados ({data?.users?.length || 0})
        </button>
        <button
          onClick={() => setTab("operarios")}
          data-testid="tab-operarios"
          className={`h-10 px-4 rounded-xl border ${tab === "operarios"
            ? "bg-[#0B2A4A] text-white border-[#0B2A4A]"
            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
          Todos los operarios ({data?.operarios_top?.length || 0})
        </button>
      </div>

      {loading && !data ? (
        <div className="brutal-card p-8 text-center text-slate-500">
          <Loader2 size={24} className="animate-spin mx-auto mb-2" /> Cargando...
        </div>
      ) : tab === "users" ? (
        <ul className="space-y-3" data-testid="users-stats-list">
          {filtered.map((u) => (
            <li key={u.user_id} className="brutal-card overflow-hidden">
              <button
                onClick={() => setExpanded(expanded === u.user_id ? null : u.user_id)}
                data-testid={`user-card-${u.user_id}`}
                className="w-full p-4 text-left flex items-center gap-3"
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                  u.role === "sadmin" ? "bg-gradient-to-br from-[#FF8A1F] to-[#F5582D]"
                  : u.role === "admin" ? "bg-[#0B2A4A]"
                  : "bg-[#F5A623]"
                }`}>
                  {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {u.name}
                    {u.role === "sadmin" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#FF8A1F] to-[#F5582D] text-white">
                        <Crown size={10} /> SAdmin
                      </span>
                    )}
                    {u.role === "admin" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#0B2A4A] text-white">
                        <ShieldCheck size={10} /> Admin
                      </span>
                    )}
                    {u.role === "employee" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                        <Shield size={10} /> Operario
                      </span>
                    )}
                    {!u.is_active && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-red-600">desactivado</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-right shrink-0">
                  <Mini label="Partes" value={u.partes_donde_participa} icon={FileSpreadsheet} />
                  <Mini label="Horas" value={Math.round(u.total_horas)} icon={Clock} />
                  <Mini label="Fotos" value={u.fotos_subidas} icon={ImageIcon} />
                </div>
                <ChevronRight size={16} className={`text-slate-400 transition-transform ml-1 ${expanded === u.user_id ? "rotate-90" : ""}`} />
              </button>

              {expanded === u.user_id && (
                <div className="border-t border-slate-200 p-5 bg-slate-50/50 space-y-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Stat label="Partes creados" value={u.partes_creados} />
                    <Stat label="Partes (participa)" value={u.partes_donde_participa} />
                    <Stat label="Horas totales" value={Math.round(u.total_horas)} />
                    <Stat label="Materiales" value={u.materiales_total} />
                    <Stat label="Obras únicas" value={u.obras_unicas} />
                    <Stat label="Fotos subidas" value={u.fotos_subidas} />
                    <Stat label="Vehículos" value={u.vehiculos.length} />
                    <Stat
                      label="Última actividad"
                      value={u.ultima_actividad ? u.ultima_actividad.slice(0, 10) : "—"}
                      small
                    />
                  </div>

                  {Object.keys(u.por_tipo).length > 0 && (
                    <Block title="Por tipo de servicio">
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(u.por_tipo).map(([k, v]) => (
                          <span key={k} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold">
                            {TIPO_LABELS[k] || k}: <span className="text-[#F5A623]">{v}</span>
                          </span>
                        ))}
                      </div>
                    </Block>
                  )}

                  {Object.keys(u.por_departamento).length > 0 && (
                    <Block title="Por departamento">
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(u.por_departamento).map(([k, v]) => (
                          <span key={k} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold">
                            {k}: <span className="text-[#0E78C2]">{v}</span>
                          </span>
                        ))}
                      </div>
                    </Block>
                  )}

                  {u.obras_top.length > 0 && (
                    <Block title="Top obras">
                      <ul className="space-y-1.5">
                        {u.obras_top.map((o, i) => (
                          <li key={i} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm">
                            <span className="truncate font-medium">{o.nombre}</span>
                            <span className="brutal-chip">{o.partes} partes</span>
                          </li>
                        ))}
                      </ul>
                    </Block>
                  )}

                  {u.vehiculos.length > 0 && (
                    <Block title="Vehículos utilizados">
                      <div className="flex flex-wrap gap-2">
                        {u.vehiculos.map((v, i) => (
                          <span key={i} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium inline-flex items-center gap-1.5">
                            <Truck size={12} /> {v}
                          </span>
                        ))}
                      </div>
                    </Block>
                  )}
                </div>
              )}
            </li>
          ))}
          {!filtered.length && (
            <li className="brutal-card p-6 text-center text-slate-500">Sin resultados.</li>
          )}
        </ul>
      ) : (
        <ul className="space-y-2" data-testid="ops-stats-list">
          {filteredOps.map((o, i) => (
            <li key={o.nombre} className="brutal-card p-4 flex items-center gap-4">
              <span className="font-display font-extrabold text-xl w-8 text-slate-400">
                {i + 1}
              </span>
              <div className="w-9 h-9 rounded-full bg-[#F5A623] text-white flex items-center justify-center font-bold text-sm">
                <Hammer size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{o.nombre}</div>
                <div className="text-xs text-slate-500">{o.partes} partes · {Math.round(o.horas)} horas</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-display font-extrabold text-[#0B2A4A]">{Math.round(o.horas)}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">horas</div>
              </div>
            </li>
          ))}
          {!filteredOps.length && (
            <li className="brutal-card p-6 text-center text-slate-500">Sin resultados.</li>
          )}
        </ul>
      )}
    </div>
  );
}

function Mini({ label, value, icon: Icon }) {
  return (
    <div className="flex flex-col items-end">
      <div className="text-base font-bold text-[#0B2A4A] inline-flex items-center gap-1">
        <Icon size={12} className="text-slate-400" /> {value}
      </div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className={`font-display font-extrabold ${small ? "text-base" : "text-2xl"} text-[#0B2A4A] leading-none`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-1">{label}</div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <div className="label-up mb-2">{title}</div>
      {children}
    </div>
  );
}

/* ===========================================================================
   ComparativeChart — horizontal bar chart of top 15 operarios per metric.
   Colors by quartile (green = top, blue, yellow, red = bottom).
   Shows the median as a vertical dashed line.
=========================================================================== */
function ComparativeChart({ data, meName }) {
  const [metric, setMetric] = useState("score"); // "horas" | "partes" | "score"

  const meKey = (meName || "").trim().toLowerCase();

  const stats = useMemo(() => {
    const vals = (data || [])
      .map((d) => Number(d[metric]) || 0)
      .filter((v) => v > 0)
      .sort((a, b) => a - b);
    if (!vals.length) return null;
    const max = vals[vals.length - 1];
    const median =
      vals.length % 2 === 0
        ? (vals[vals.length / 2 - 1] + vals[vals.length / 2]) / 2
        : vals[Math.floor(vals.length / 2)];
    const q1 = vals[Math.floor(vals.length * 0.25)];
    const q3 = vals[Math.floor(vals.length * 0.75)];
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    return { max, median, q1, q3, avg, count: vals.length };
  }, [data, metric]);

  const top = useMemo(() => {
    return [...(data || [])]
      .sort((a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0))
      .slice(0, 15);
  }, [data, metric]);

  if (!stats) {
    return (
      <section className="brutal-card p-5 text-center text-slate-500 text-sm">
        Sin datos para comparar todavía.
      </section>
    );
  }

  const tier = (v) => {
    if (v >= stats.q3) return "top";
    if (v >= stats.median) return "good";
    if (v >= stats.q1) return "mid";
    return "low";
  };

  const TIER_STYLES = {
    top: { bar: "bg-gradient-to-r from-emerald-500 to-emerald-400", chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    good: { bar: "bg-gradient-to-r from-[#0E78C2] to-[#3B9EE3]", chip: "bg-blue-100 text-blue-700 border-blue-200" },
    mid: { bar: "bg-gradient-to-r from-[#F5A623] to-[#FFC740]", chip: "bg-amber-100 text-amber-800 border-amber-200" },
    low: { bar: "bg-gradient-to-r from-red-500 to-red-400", chip: "bg-red-100 text-red-700 border-red-200" },
  };

  const medianPct = (stats.median / stats.max) * 100;
  const unit = metric === "horas" ? "h" : metric === "partes" ? "p" : "";

  return (
    <section className="brutal-card p-5" data-testid="comparative-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-up text-[#F5A623] inline-flex items-center gap-1.5">
            <BarChart3 size={12} /> Comparativa
          </div>
          <h2 className="font-display font-extrabold text-xl mt-1">
            Rendimiento entre operarios
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {metric === "score"
              ? "Score combinado (50% horas + 30% partes + 20% materiales), normalizado 0-100."
              : `Top 15 ordenados por ${metric === "horas" ? "horas trabajadas" : "partes registrados"}.`}
            {" "}Color por cuartil — verde lidera, rojo está bajo del 25%.
          </p>
        </div>
        <div className="flex gap-1.5 p-1 rounded-xl bg-slate-100 border border-slate-200">
          <MetricBtn active={metric === "score"} onClick={() => setMetric("score")} testId="metric-score">
            <Star size={12} /> Score
          </MetricBtn>
          <MetricBtn active={metric === "horas"} onClick={() => setMetric("horas")} testId="metric-horas">
            <Clock size={12} /> Horas
          </MetricBtn>
          <MetricBtn active={metric === "partes"} onClick={() => setMetric("partes")} testId="metric-partes">
            <FileSpreadsheet size={12} /> Partes
          </MetricBtn>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <SummaryStat label="Mediana" value={Math.round(stats.median)} accent="text-[#0E78C2]" icon={TrendingUp} />
        <SummaryStat label="Media" value={Math.round(stats.avg)} />
        <SummaryStat label="Máximo" value={Math.round(stats.max)} accent="text-emerald-600" />
        <SummaryStat label="Operarios" value={stats.count} />
      </div>

      <div className="flex flex-wrap gap-2 mb-5 text-[10px] font-semibold uppercase tracking-wider">
        <LegendChip color="bg-emerald-500">Top 25%</LegendChip>
        <LegendChip color="bg-[#0E78C2]">Sobre mediana</LegendChip>
        <LegendChip color="bg-[#F5A623]">Bajo mediana</LegendChip>
        <LegendChip color="bg-red-500">Bottom 25%</LegendChip>
        {meKey && <LegendChip color="bg-purple-500">Tú</LegendChip>}
      </div>

      <div className="relative" data-testid="chart-bars">
        <div
          className="absolute top-0 bottom-0 border-l-2 border-dashed border-[#0B2A4A] z-10 pointer-events-none"
          style={{ left: `calc(140px + (100% - 140px) * ${medianPct / 100})` }}
        >
          <div className="absolute -top-6 -translate-x-1/2 whitespace-nowrap">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#0B2A4A] bg-white px-1.5 py-0.5 rounded border border-[#0B2A4A]">
              Mediana: {Math.round(stats.median)}{unit}
            </span>
          </div>
        </div>

        <ul className="space-y-2 pt-2">
          {top.map((o, i) => {
            const v = Number(o[metric]) || 0;
            const pct = (v / stats.max) * 100;
            const t = tier(v);
            const style = TIER_STYLES[t];
            const isMe = meKey && (o.nombre || "").toLowerCase().includes(meKey.split(" ")[0]);
            return (
              <li key={o.nombre} className={`flex items-center gap-3 ${isMe ? "bg-purple-50 -mx-2 px-2 py-1 rounded-lg border border-purple-200" : ""}`} data-testid={`bar-${i}`}>
                <div className="w-[140px] shrink-0 text-right pr-2">
                  <div className={`text-sm font-semibold truncate ${isMe ? "text-purple-700" : "text-[#0B2A4A]"}`} title={o.nombre}>
                    {isMe && <Star size={11} className="inline mr-1 fill-purple-500 text-purple-500" />}
                    {o.nombre}
                  </div>
                </div>
                <div className="flex-1 relative h-7 bg-slate-100 rounded-lg overflow-hidden">
                  <div
                    className={`h-full ${isMe ? "bg-gradient-to-r from-purple-500 to-purple-400" : style.bar} rounded-lg transition-all flex items-center justify-end px-2`}
                    style={{ width: `${Math.max(pct, 4)}%` }}
                  >
                    <span className="text-[11px] font-bold text-white whitespace-nowrap">
                      {metric === "score" ? v.toFixed(1) : Math.round(v)}
                    </span>
                  </div>
                </div>
                <div className="w-7 shrink-0 text-center">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold border ${style.chip}`}>
                    {i + 1}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/* ===========================================================================
   ServiceMixChart — stacked bar per top operario, showing % of each tipo
=========================================================================== */
const TIPO_COLORS = {
  AVERIA: "#EF4444",
  OBRA: "#F5A623",
  MANTENIMIENTO: "#10B981",
  SERVICIO_PUNTUAL: "#A855F7",
  VARIOS: "#94A3B8",
};
const TIPO_NAMES = {
  AVERIA: "Avería",
  OBRA: "Obra",
  MANTENIMIENTO: "Mantenim.",
  SERVICIO_PUNTUAL: "Serv. Puntual",
  VARIOS: "Varios",
};

function ServiceMixChart({ data, meName }) {
  const meKey = (meName || "").trim().toLowerCase();
  const top = useMemo(() =>
    [...(data || [])]
      .filter((o) => o.por_tipo && Object.keys(o.por_tipo).length > 0)
      .sort((a, b) => (b.partes || 0) - (a.partes || 0))
      .slice(0, 10),
  [data]);

  if (!top.length) return null;

  return (
    <section className="brutal-card p-5" data-testid="service-mix-chart">
      <div className="label-up text-[#F5A623] inline-flex items-center gap-1.5">
        <Layers size={12} /> Mix de servicios
      </div>
      <h2 className="font-display font-extrabold text-xl mt-1">
        ¿Quién hace qué?
      </h2>
      <p className="text-xs text-slate-500 mt-1 mb-4">
        Distribución por tipo de servicio en los partes de cada operario (top 10).
      </p>

      <div className="flex flex-wrap gap-2 mb-4 text-[10px] font-semibold uppercase tracking-wider">
        {Object.entries(TIPO_NAMES).map(([k, n]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-slate-600">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: TIPO_COLORS[k] }} />
            {n}
          </span>
        ))}
      </div>

      <ul className="space-y-2">
        {top.map((o, i) => {
          const total = Object.values(o.por_tipo).reduce((a, b) => a + b, 0) || 1;
          const isMe = meKey && (o.nombre || "").toLowerCase().includes(meKey.split(" ")[0]);
          return (
            <li key={o.nombre} className={`flex items-center gap-3 ${isMe ? "bg-purple-50 -mx-2 px-2 py-1 rounded-lg border border-purple-200" : ""}`}>
              <div className="w-[140px] shrink-0 text-right pr-2">
                <div className={`text-sm font-semibold truncate ${isMe ? "text-purple-700" : "text-[#0B2A4A]"}`} title={o.nombre}>
                  {isMe && <Star size={11} className="inline mr-1 fill-purple-500 text-purple-500" />}
                  {o.nombre}
                </div>
                <div className="text-[10px] text-slate-500">{total} partes</div>
              </div>
              <div className="flex-1 h-6 rounded-lg overflow-hidden flex border border-slate-200">
                {Object.entries(o.por_tipo)
                  .sort((a, b) => b[1] - a[1])
                  .map(([t, n]) => {
                    const pct = (n / total) * 100;
                    return (
                      <div
                        key={t}
                        title={`${TIPO_NAMES[t] || t}: ${n} (${pct.toFixed(0)}%)`}
                        style={{ width: `${pct}%`, backgroundColor: TIPO_COLORS[t] || "#94A3B8" }}
                        className="flex items-center justify-center text-[10px] font-bold text-white"
                      >
                        {pct > 10 ? `${pct.toFixed(0)}%` : ""}
                      </div>
                    );
                  })}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ===========================================================================
   MonthlyEvolutionChart — SVG line chart showing top operarios over months
=========================================================================== */
const LINE_COLORS = ["#0E78C2", "#F5A623", "#10B981", "#A855F7", "#EF4444", "#0B2A4A"];

function MonthlyEvolutionChart({ data, meName, fechaDesde, fechaHasta }) {
  const [metric, setMetric] = useState("horas");
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  const meKey = (meName || "").trim().toLowerCase();

  // Top 6 operarios by horas
  const candidates = useMemo(() => {
    const tops = [...(data || [])]
      .sort((a, b) => (b.horas || 0) - (a.horas || 0))
      .slice(0, 6);
    // ensure current user is included if matches any
    if (meKey) {
      const meEntry = (data || []).find((o) =>
        (o.nombre || "").toLowerCase().includes(meKey.split(" ")[0]),
      );
      if (meEntry && !tops.find((t) => t.nombre === meEntry.nombre)) {
        tops[tops.length - 1] = meEntry;
      }
    }
    return tops;
  }, [data, meKey]);

  useEffect(() => {
    if (!candidates.length) return;
    setLoading(true);
    const nombres = candidates.map((c) => c.nombre).join(",");
    api.get("/sadmin/stats/monthly", {
      params: {
        nombres,
        fecha_desde: fechaDesde || undefined,
        fecha_hasta: fechaHasta || undefined,
      },
    }).then((r) => setSeries(r.data.series || []))
      .catch(() => setSeries([]))
      .finally(() => setLoading(false));
  }, [candidates, fechaDesde, fechaHasta]);

  // Build chart data
  const allMonths = useMemo(() => {
    const s = new Set();
    for (const sr of series) {
      for (const p of sr.points) s.add(p.month);
    }
    return Array.from(s).sort();
  }, [series]);

  const maxValue = useMemo(() => {
    let m = 0;
    for (const sr of series) {
      for (const p of sr.points) {
        const v = p[metric] || 0;
        if (v > m) m = v;
      }
    }
    return m || 1;
  }, [series, metric]);

  const W = 700;
  const H = 240;
  const PAD = { l: 40, r: 16, t: 16, b: 30 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const xFor = (i) => PAD.l + (allMonths.length <= 1 ? innerW / 2 : (i / (allMonths.length - 1)) * innerW);
  const yFor = (v) => PAD.t + innerH - (v / maxValue) * innerH;

  if (!candidates.length) return null;

  return (
    <section className="brutal-card p-5" data-testid="monthly-evolution-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-up text-[#F5A623] inline-flex items-center gap-1.5">
            <LineChart size={12} /> Evolución temporal
          </div>
          <h2 className="font-display font-extrabold text-xl mt-1">
            Mes a mes (top 6)
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {metric === "horas" ? "Horas trabajadas" : "Partes registrados"} por mes de los 6 operarios más activos.
          </p>
        </div>
        <div className="flex gap-1.5 p-1 rounded-xl bg-slate-100 border border-slate-200">
          <MetricBtn active={metric === "horas"} onClick={() => setMetric("horas")} testId="line-horas">
            <Clock size={12} /> Horas
          </MetricBtn>
          <MetricBtn active={metric === "partes"} onClick={() => setMetric("partes")} testId="line-partes">
            <FileSpreadsheet size={12} /> Partes
          </MetricBtn>
        </div>
      </div>

      {loading ? (
        <div className="h-60 flex items-center justify-center text-slate-400">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : !allMonths.length ? (
        <div className="h-40 flex items-center justify-center text-slate-500 text-sm">
          Sin datos mensuales para mostrar
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ minWidth: 480 }}>
              {/* Y grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((p) => (
                <g key={p}>
                  <line
                    x1={PAD.l} x2={W - PAD.r}
                    y1={PAD.t + innerH * (1 - p)} y2={PAD.t + innerH * (1 - p)}
                    stroke="#E2E8F0" strokeWidth="1" strokeDasharray="3 3"
                  />
                  <text
                    x={PAD.l - 6} y={PAD.t + innerH * (1 - p) + 4}
                    textAnchor="end" fontSize="10" fill="#64748B"
                  >
                    {Math.round(maxValue * p)}
                  </text>
                </g>
              ))}
              {/* X axis labels */}
              {allMonths.map((m, i) => (
                <text key={m} x={xFor(i)} y={H - 10} textAnchor="middle" fontSize="9" fill="#64748B">
                  {m.slice(2)}
                </text>
              ))}
              {/* Lines */}
              {series.map((sr, idx) => {
                const isMe = meKey && (sr.nombre || "").toLowerCase().includes(meKey.split(" ")[0]);
                const color = isMe ? "#A855F7" : LINE_COLORS[idx % LINE_COLORS.length];
                const pointMap = new Map(sr.points.map((p) => [p.month, p[metric] || 0]));
                const path = allMonths
                  .map((m, i) => {
                    const v = pointMap.get(m) ?? 0;
                    return `${i === 0 ? "M" : "L"} ${xFor(i)} ${yFor(v)}`;
                  })
                  .join(" ");
                return (
                  <g key={sr.nombre}>
                    <path
                      d={path}
                      fill="none"
                      stroke={color}
                      strokeWidth={isMe ? 3 : 2}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                    {allMonths.map((m, i) => {
                      const v = pointMap.get(m) ?? 0;
                      if (v === 0) return null;
                      return (
                        <circle key={m} cx={xFor(i)} cy={yFor(v)} r={isMe ? 4 : 3} fill={color}>
                          <title>{`${sr.nombre} · ${m}: ${Math.round(v)}`}</title>
                        </circle>
                      );
                    })}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4 text-xs">
            {series.map((sr, idx) => {
              const isMe = meKey && (sr.nombre || "").toLowerCase().includes(meKey.split(" ")[0]);
              const color = isMe ? "#A855F7" : LINE_COLORS[idx % LINE_COLORS.length];
              return (
                <span key={sr.nombre} className={`inline-flex items-center gap-1.5 font-semibold ${isMe ? "text-purple-700" : "text-slate-700"}`}>
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                  {isMe && <Star size={11} className="fill-purple-500 text-purple-500" />}
                  {sr.nombre}
                </span>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

function MetricBtn({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-9 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${
        active ? "bg-[#0B2A4A] text-white shadow-sm" : "text-slate-600 hover:bg-white"
      }`}
    >
      {children}
    </button>
  );
}

function SummaryStat({ label, value, accent = "text-[#0B2A4A]", icon: Icon }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-slate-400" />}
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      </div>
      <div className={`font-display font-extrabold text-2xl ${accent} leading-none mt-1`}>{value}</div>
    </div>
  );
}

function LegendChip({ color, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-600">
      <span className={`w-3 h-3 rounded-sm ${color}`} />
      {children}
    </span>
  );
}
